import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Card, Tag, Row, Col, Empty, Modal } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

function BlogPostModal({ post, open, onClose, siteInfo }) {
  const brandColor = siteInfo?.brand_color || '#1890ff';
  if (!post) return null;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
    >
      {post.featured_image && (
        <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden', maxHeight: 300 }}>
          <img src={post.featured_image} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      )}
      <Title level={3} style={{ marginBottom: 8 }}>{post.title}</Title>
      {post.published_at && (
        <Text type="secondary" style={{ fontSize: 13, marginBottom: 16, display: 'block' }}>
          <CalendarOutlined /> {new Date(post.published_at).toLocaleDateString()}
        </Text>
      )}
      {post.tags?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {post.tags.map(tag => <Tag key={tag} color={brandColor}>{tag}</Tag>)}
        </div>
      )}
      <div dangerouslySetInnerHTML={{ __html: post.content }} style={{ lineHeight: 1.8 }} />
    </Modal>
  );
}

export default function SectionBlog({ section, siteInfo }) {
  const { t } = useTranslation();
  const data = section.content_data || {};
  const posts = data.blog_posts || [];
  const [selectedPost, setSelectedPost] = useState(null);

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';
  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {section.title}
      </Title>
      {section.subtitle && (
        <Paragraph style={{ textAlign: isMinimal ? 'left' : 'center', color: '#666', marginBottom: 32 }}>
          {section.subtitle}
        </Paragraph>
      )}

      {posts.length === 0 ? (
        <Empty description={t('sections.blog.noPosts')} />
      ) : (
        <Row gutter={[24, 24]}>
          {posts.map(post => (
            <Col key={post.id} xs={24} sm={12} md={8}>
              <Card
                hoverable
                onClick={() => setSelectedPost(post)}
                style={{ borderRadius: cardRadius, height: '100%', overflow: 'hidden' }}
                cover={post.featured_image ? (
                  <div style={{ overflow: 'hidden' }}>
                    <img alt={post.title} src={post.featured_image} style={{ width: '100%', display: 'block' }} />
                  </div>
                ) : (
                  <div style={{
                    height: 180,
                    background: `linear-gradient(135deg, ${brandColor}22, ${brandColor}44)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: `${cardRadius}px ${cardRadius}px 0 0`,
                    fontSize: 48,
                  }}>
                    📝
                  </div>
                )}
                styles={{ body: { padding: 16 } }}
              >
                <Title level={5} style={{ marginBottom: 6 }} ellipsis={{ rows: 2 }}>{post.title}</Title>
                {post.published_at && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    <CalendarOutlined /> {new Date(post.published_at).toLocaleDateString()}
                  </Text>
                )}
                {post.excerpt && (
                  <Paragraph ellipsis={{ rows: 3 }} style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{post.excerpt}</Paragraph>
                )}
                {post.tags?.length > 0 && (
                  <div>{post.tags.slice(0, 3).map(tag => <Tag key={tag} style={{ fontSize: 11 }}>{tag}</Tag>)}</div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <BlogPostModal
        post={selectedPost}
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        siteInfo={siteInfo}
      />
    </div>
  );
}
